import { AlertTriangle, ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { monthLabel } from '../list/list-query';
import type { Issue } from './types';

type Kind = 'block' | 'warn';

/** Where each line opens: the list that holds what it counts (spec §1.6, «ogni riga apre il suo elenco»). */
export function issueTarget(code: string, month: string): string {
  const reports = (status?: string) =>
    `/expenses/reports?period=${month}${status === undefined ? '' : `&status=${status}`}`;
  switch (code) {
    case 'REPORTS_PENDING':
      return reports('submitted');
    case 'REPORTS_SENT_BACK':
      return reports('more_info_requested');
    case 'DRAFT_REPORTS':
      return reports('draft');
    case 'SOLE_APPROVER':
      return reports('accepted');
    case 'DOCUMENTS_AWAITED':
      return `/expenses?period=${month}&document=awaited`;
    default:
      return `/expenses?period=${month}`;
  }
}

function IssueRow({
  issue,
  kind,
  month,
}: {
  readonly issue: Issue;
  readonly kind: Kind;
  readonly month: string;
}) {
  const { t, i18n } = useTranslation('closing');
  const Mark = kind === 'block' ? X : AlertTriangle;
  const withReports = issue.reportNumbers.length > 0;
  const label = (t as (key: string, options: object) => string)(`issue.${issue.code}`, {
    count: issue.count,
    month: monthLabel(month, i18n.resolvedLanguage),
  });
  return (
    <li
      data-testid={`issue-${issue.code}`}
      className="border-border flex items-center gap-x-3 border-t py-2 first:border-t-0"
    >
      <Mark
        size={16}
        aria-hidden
        className={`shrink-0 ${kind === 'block' ? 'text-destructive' : 'text-warning'}`}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm">{label}</span>
        {withReports ? (
          <span className="text-muted-foreground flex flex-wrap gap-x-2 text-sm">
            {issue.reportIds.map((id, index) => (
              <Link
                key={id}
                to={`/expenses/reports/${encodeURIComponent(id)}`}
                className="text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                {issue.reportNumbers[index]}
              </Link>
            ))}
          </span>
        ) : null}
      </div>
      <Link
        to={issueTarget(issue.code, month)}
        className="border-border hover:bg-muted inline-flex shrink-0 items-center gap-1 rounded-md border px-2.5 py-1 text-sm"
      >
        {withReports ? t('open') : t('list')}
        <ChevronRight size={14} aria-hidden />
      </Link>
    </li>
  );
}

/** «Cosa blocca» and «Da controllare (non blocca)»: a panel of lines, or a line saying all is clear. */
export function IssuePanel({
  title,
  issues,
  kind,
  month,
  clear,
}: {
  readonly title: string;
  readonly issues: readonly Issue[];
  readonly kind: Kind;
  readonly month: string;
  readonly clear?: string;
}) {
  if (issues.length === 0 && clear === undefined) return null;
  return (
    <section
      aria-label={title}
      className="border-border bg-card flex flex-col gap-1 rounded-lg border p-4"
    >
      <h3 className="text-sm font-semibold">{title}</h3>
      {issues.length === 0 ? (
        <p className="text-muted-foreground text-sm">{clear}</p>
      ) : (
        <ul className="flex flex-col">
          {issues.map((issue) => (
            <IssueRow key={issue.code} issue={issue} kind={kind} month={month} />
          ))}
        </ul>
      )}
    </section>
  );
}
